/**
 * Triple Exponential Moving Average (TEMA)
 *
 * Reduces the lag inherent in a single EMA by applying EMA three times:
 * TEMA = 3×EMA₁ − 3×EMA₂ + EMA₃  (where EMA₂ = EMA of EMA₁, etc.).
 *
 * Category: Trend | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { getSource, computeEMA } from "./sma";

export const temaIndicator: IndicatorDefinition = {
  id: "tema",
  name: "Triple Exponential Moving Average",
  shortName: "TEMA",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "period", label: "Period", type: "number", default: 20, min: 1, max: 500 },
    { key: "source", label: "Source", type: "select", default: "close", options: [
      { label: "Close", value: "close" }, { label: "Open", value: "open" },
      { label: "High", value: "high" }, { label: "Low", value: "low" },
      { label: "HL2", value: "hl2" }, { label: "HLC3", value: "hlc3" },
    ]},
    { key: "color", label: "Color", type: "color", default: "#E040FB" },
  ],
  plots: [
    { key: "tema", label: "TEMA", color: "#E040FB", style: "line", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, source = "close" } = params;
    const src = getSource(candles, source);
    const ema1 = computeEMA(src, period);
    const validEma1 = ema1.filter(v => !isNaN(v));
    const ema2 = computeEMA(validEma1, period);
    const validEma2 = ema2.filter(v => !isNaN(v));
    const ema3 = computeEMA(validEma2, period);

    const data: { time: number; value: number }[] = [];
    const startIdx = 3 * (period - 1);

    let e2Idx = 0;
    let e3Idx = 0;
    const ema1Start = period - 1;
    const ema2Start = ema1Start + period - 1;
    const ema3Start = ema2Start + period - 1;

    for (let i = ema3Start; i < candles.length; i++) {
      e2Idx = i - ema1Start - (period - 1);
      e3Idx = i - ema3Start;

      if (e2Idx >= 0 && e2Idx < validEma2.length && e3Idx >= 0 && e3Idx < ema3.length && !isNaN(ema3[e3Idx])) {
        // TEMA = 3*EMA1 - 3*EMA2 + EMA3
        const tema = 3 * ema1[i] - 3 * validEma1[i - ema1Start] + ema3[e3Idx];
        // Simplified: use aligned EMA values
        data.push({ time: candles[i].time, value: tema });
      }
    }

    // Fallback: simpler calculation if alignment is complex
    if (data.length === 0 && candles.length > startIdx) {
      const e1 = computeEMA(src, period);
      const e1Valid = e1.filter(v => !isNaN(v));
      const e2 = computeEMA(e1Valid, period);
      const e2Valid = e2.filter(v => !isNaN(v));
      const e3f = computeEMA(e2Valid, period);

      for (let i = 0; i < e3f.length; i++) {
        if (!isNaN(e3f[i])) {
          const candleIdx = startIdx + i;
          if (candleIdx < candles.length) {
            const tema = 3 * e1[candleIdx] - 3 * e2Valid[i + (period - 1)] + e3f[i];
            if (!isNaN(tema)) {
              data.push({ time: candles[candleIdx].time, value: tema });
            }
          }
        }
      }
    }

    return [{ key: "tema", data }];
  },
};
